import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "state/state";
import { changePage } from "state/cart/actions";
import { updateUser, clearCartUser } from "state/user/actions";
import { getProduct } from "state/repo";
import { getConfigurationProducts } from "helpers/configurationHelper";
import { getItemPrice, getCurrency, getPriceNumeric } from "helpers/priceHelper";
import { isDiscount } from "helpers/discountHelpers";
import { api } from "api/Api";
import { TrackEvent } from "helpers/Tracking";
import { getImage } from "utils/utils";
import TextField from "components/elements/TextField";
import Row from "components/layout/Sidebar/Row";
import { Dictionary, Product } from "typings/repo";
import { ShippingMethodViewModel } from "api/CheckoutApi";
import {
    Overlay, Modal, ModalHeader, BackButton, CheckoutBody,
    StepsCol, CartCol, CartImage,
    SectionLabel, CartItemRow, CartItemDetails, CartItemQty, CartItemPrice,
    CartTotals, PriceLine, TotalLine,
    StepCard, StepHeader, StepNumber, StepTitle, StepSummary, EditLink,
    StepBody, StepActions, PrimaryButton, AddressDivider, ShipToggleLabel,
    DeliveryOptions, DeliveryOption, DeliveryLabel, DeliveryPrice,
    SummaryBox, SummaryLine, SummaryDivider, SummaryTotal, ErrorBanner,
} from "./DanishCheckout.style";

const STEPS = ["Adresse", "Levering", "Betaling"];

interface DanishCheckoutProps {
    cancelError?: boolean;
    onCancelErrorDismiss?: () => void;
}

const DanishCheckout: React.FC<DanishCheckoutProps> = ({ cancelError, onCancelErrorDismiss }) => {
    const state = useSelector((s: RootState) => s);
    const dispatch = useDispatch();
    const { repo, configuration, cart, user } = state;
    const tokens = repo.translations;
    const stepFormRef = React.createRef<HTMLFormElement>();
    const [step, setStep] = React.useState(0);
    const [shippingMethods, setShippingMethods] = React.useState<ShippingMethodViewModel[]>([]);
    const [delivery, setDelivery] = React.useState<string>("");
    const [shipToDifferent, setShipToDifferent] = React.useState(false);
    const [shippingAddress, setShippingAddress] = React.useState({ address: "", zip: "", city: "" });
    const [apiError, setApiError] = React.useState<string | null>(null);

    // Restore local form state (shipToDifferent, shippingAddress, delivery) on cancel return
    React.useEffect(() => {
        if (!cancelError) return;
        const saved = sessionStorage.getItem("dk_checkout_user");
        if (!saved) return;
        try {
            const parsed = JSON.parse(saved);
            if (parsed.shipToDifferent) setShipToDifferent(parsed.shipToDifferent);
            if (parsed.shippingAddress) setShippingAddress(parsed.shippingAddress);
            if (parsed.delivery) setDelivery(parsed.delivery);
        } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cancelError]);

    React.useEffect(() => {
        const toOrderLines = (dict: Dictionary<number> | undefined) =>
            dict ? Object.keys(dict).filter(k => dict[k] > 0).map(k => ({ sku: k, quantity: dict[k] })) : [];

        const orderLines = [
            ...toOrderLines(getConfigurationProducts(configuration, repo)),
            ...toOrderLines(cart.accessories),
            ...toOrderLines(cart.additional),
        ];

        api.checkoutApi.getShippingMethods(orderLines).then((methods) => {
            setShippingMethods(methods);
            if (methods.length > 0) setDelivery(methods[0].id);
        }).catch(() => {
            setApiError("Kunne ikke hente leveringsmetoder. Prøv at genindlæse siden.");
        });
    }, [configuration, repo, cart.accessories, cart.additional]);

    const products = getConfigurationProducts(configuration, repo);
    const discount = isDiscount(repo, cart.currency);
    const currencyFormat = getCurrency(cart.currency, repo);
    const fmt = (n: number) => currencyFormat.replace("{0}", n.toLocaleString(repo.langCode));

    const configNumeric = getPriceNumeric(configuration, repo, cart.currency, cart.accessories, cart.additional, discount);
    const selectedMethod = shippingMethods.find(m => m.id === delivery);
    const isPickup = selectedMethod?.id === "Pickup";
    const deliveryCost = selectedMethod?.price ?? 0;
    const grandTotal = configNumeric + deliveryCost;

    const buildCartItem = (product: Product, amount: number, keyPrefix: string) => {
        const price = getItemPrice(product, cart.currency, repo.langCode, amount);
        const discountPrice = getItemPrice(product, cart.currency, repo.langCode, amount, true);
        return (
            <CartItemRow key={keyPrefix + product.id}>
                <img src={getImage(product.imageUrl, "THUMBNAIL")} width={44} height={44} alt={product.title} />
                <CartItemDetails>
                    <div className="sku">{product.id}</div>
                    <div className="title">{product.title}</div>
                </CartItemDetails>
                <CartItemQty>×{amount}</CartItemQty>
                <CartItemPrice>
                    {discount && price !== discountPrice
                        ? <><div className="original">{price}</div><div className="amount">{discountPrice}</div></>
                        : <div className="amount">{price}</div>}
                </CartItemPrice>
            </CartItemRow>
        );
    };

    const renderCartLines = (elements: Dictionary<number> | undefined, keyPrefix: string) => {
        if (!elements) return null;
        return Object.keys(elements).filter(id => elements[id] > 0).map(id => {
            const product = getProduct(repo, id);
            return product ? buildCartItem(product, elements[id], keyPrefix) : null;
        });
    };

    const advanceStep = () => {
        if (stepFormRef.current && !stepFormRef.current.checkValidity()) {
            stepFormRef.current.reportValidity();
            return;
        }
        setStep(s => Math.min(s + 1, STEPS.length - 1));
    };

    const checkOut = async () => {
        if (stepFormRef.current && !stepFormRef.current.checkValidity()) {
            stepFormRef.current.reportValidity();
            return;
        }
        setApiError(null);
        TrackEvent("CART", "dk-checkout", "success");

        // Save configuration so it can be retrieved on the return/thank-you screen
        let storageId: string;
        try {
            storageId = await api.configurationApi.saveConfiguration({
                project: repo.project,
                configuration,
                accessories: cart.accessories,
                additional: cart.additional,
            });
        } catch {
            setApiError("Kunne ikke gemme konfigurationen. Prøv venligst igen.");
            return;
        }

        // Build order lines from configuration + accessories + additional
        const toOrderLines = (dict: Dictionary<number> | undefined) =>
            dict ? Object.keys(dict).filter(k => dict[k] > 0).map(k => ({ sku: k, quantity: dict[k] })) : [];

        const orderLines = [
            ...toOrderLines(products),
            ...toOrderLines(cart.accessories),
            ...toOrderLines(cart.additional),
        ];

        const billing = {
            firstName: user.firstname ?? "",
            lastName: user.lastname ?? "",
            emailAddress: user.email ?? "",
            phoneNumber: user.phone ?? "",
            company: user.firmname,
            addressLine1: user.address ?? "",
            postalCode: user.zip ?? "",
            city: user.city ?? "",
            countryId: 6, // Denmark
        };

        const shipping = (!isPickup && shipToDifferent) ? {
            firstName: user.firstname ?? "",
            lastName: user.lastname ?? "",
            emailAddress: user.email ?? "",
            phoneNumber: user.phone ?? "",
            addressLine1: shippingAddress.address,
            postalCode: shippingAddress.zip,
            city: shippingAddress.city,
            countryId: 6,
        } : undefined;

        try {
            const result =await api.checkoutApi.initializeCheckout({
                orderLines,
                information: {
                    billing,
                    shippingAsBilling: isPickup || !shipToDifferent,
                    shipping,
                    note: user.comment,
                },
                shippingMethod: selectedMethod!,
                acceptUrl: `${window.location.origin}${window.location.pathname}?order=${encodeURIComponent(storageId)}`,
                cancelUrl: `${window.location.origin}${window.location.pathname}?checkout=${storageId}`,
                newsletter: false,
                termsUrl: "https://flex1one.dk/profil/handelsbetingelser/",
            });
        
        if (result && result.redirectUri) {
            // Persist form state so it can be restored if the user cancels at the payment gateway
            sessionStorage.setItem("dk_checkout_user", JSON.stringify({
                user,
                shipToDifferent,
                shippingAddress,
                delivery,
            }));
            window.location.href = result.redirectUri;
        } else {
            //setFormSuccess(true);
            dispatch(clearCartUser());
        }
        } catch {
            setApiError("Der opstod en fejl under checkout. Prøv venligst igen.");
            return;
        }
    };

    const stepSummary = (i: number): string => {
        switch (i) {
            case 0: return [user.firstname, user.lastname].filter(Boolean).join(" ") + (user.address ? ` · ${user.address}` : "");
            case 1: return selectedMethod ? `${tokens[selectedMethod.name] ?? selectedMethod.name} · ${fmt(deliveryCost)}` : "";
            default: return "";
        }
    };

    const renderStepBody = (i: number) => {
        switch (i) {
            case 0:
                return (
                    <>
                        <Row columns={2}>
                            <TextField id="dk-firstname" autoComplete="given-name" title={tokens.FIRST_NAME} required min={2}
                                value={user.firstname} onChange={(e) => dispatch(updateUser(e, "firstname"))} />
                            <TextField id="dk-lastname" autoComplete="family-name" title={tokens.LAST_NAME} required min={2}
                                value={user.lastname} onChange={(e) => dispatch(updateUser(e, "lastname"))} />
                        </Row>
                        <Row columns={2}>
                            <TextField id="dk-email" autoComplete="email" type="email" title={tokens.EMAIL} required
                                value={user.email} onChange={(e) => dispatch(updateUser(e, "email"))} />
                            <TextField id="dk-phone" autoComplete="tel" type="number" title={tokens.PHONE} min={8} required
                                value={user.phone} onChange={(e) => dispatch(updateUser(e, "phone"))} />
                        </Row>
                        <TextField id="dk-company" autoComplete="organization" title={tokens.FIRM_NAME} min={2}
                            value={user.firmname} onChange={(e) => dispatch(updateUser(e, "firmname"))} />
                        <AddressDivider>{tokens.ADDRESS ?? "Faktureringsadresse"}</AddressDivider>
                        <TextField id="dk-address" autoComplete="street-address" title={tokens.ADDRESS} min={4} required
                            value={user.address} onChange={(e) => dispatch(updateUser(e, "address"))} />
                        <Row columns={2}>
                            <TextField id="dk-zip" autoComplete="postal-code" title={tokens.ZIP} min={4} required pattern={"^[A-Za-z0-9]+$"}
                                value={user.zip} onChange={(e) => dispatch(updateUser(e, "zip"))} />
                            <TextField id="dk-city" autoComplete="address-level2" title={tokens.CITY} min={2} required
                                value={user.city} onChange={(e) => dispatch(updateUser(e, "city"))} />
                        </Row>
                        {!isPickup && ( 
                            <>
                                <ShipToggleLabel>
                                    <input type="checkbox" checked={shipToDifferent}
                                        onChange={(e) => setShipToDifferent(e.target.checked)} />
                                    {tokens.SHIP_TO_DIFFERENT ?? "Send til en anden adresse"}
                                </ShipToggleLabel>
                                {shipToDifferent && (
                                    <>
                                        <AddressDivider>{tokens.SHIPPING_ADDRESS ?? "Forsendelsesadresse"}</AddressDivider>
                                        <TextField id="dk-ship-address" autoComplete="shipping street-address" title={tokens.ADDRESS} min={4} required
                                            value={shippingAddress.address} onChange={(e) => setShippingAddress(a => ({ ...a, address: e }))} />
                                        <Row columns={2}>
                                            <TextField id="dk-ship-zip" autoComplete="shipping postal-code" title={tokens.ZIP} min={4} required pattern={"^[A-Za-z0-9]+$"}
                                                value={shippingAddress.zip} onChange={(e) => setShippingAddress(a => ({ ...a, zip: e }))} />
                                            <TextField id="dk-ship-city" autoComplete="shipping address-level2" title={tokens.CITY} min={2} required
                                                value={shippingAddress.city} onChange={(e) => setShippingAddress(a => ({ ...a, city: e }))} />
                                        </Row>
                                    </>
                                )}
                            </>
                        )}
                    </>
                );
            case 1:
                return (
                    <DeliveryOptions>
                        {shippingMethods.map((method) => {
                            const label = tokens[method.name] ?? method.name;
                            const price = method.price === 0 ? "0,-" : fmt(method.price);
                            const selected = delivery === method.id;
                            return (
                                <DeliveryOption key={method.id} selected={selected}>
                                    <DeliveryLabel>
                                        <input type="radio" name="dk-delivery" value={method.id} checked={selected}
                                            onChange={() => setDelivery(method.id)} />
                                        <span>{label}</span>
                                    </DeliveryLabel>
                                    <DeliveryPrice>{price}</DeliveryPrice>
                                </DeliveryOption>
                            );
                        })}
                    </DeliveryOptions>
                );
            case 2:
                return (
                    <>
                        <SummaryBox>
                            <SummaryLine>
                                <span>Konfiguration</span>
                                <span>{fmt(configNumeric)}</span>
                            </SummaryLine>
                            <SummaryDivider>
                                <span>{selectedMethod ? (tokens[selectedMethod.name] ?? selectedMethod.name) : ""}</span>
                                <span>{fmt(deliveryCost)}</span>
                            </SummaryDivider>
                            <SummaryTotal>
                                <span>{tokens.TOTAL_EX_MVA}</span>
                                <span>{fmt(grandTotal)}</span>
                            </SummaryTotal>
                        </SummaryBox>
                        <TextField title={tokens.COMMENT} value={user.comment}
                            onChange={(e) => dispatch(updateUser(e, "comment"))} />
                    </>
                );
            default: return null;
        }
    };

    const renderAccordion = () => (
        <form ref={stepFormRef} onSubmit={(e) => e.preventDefault()}>
            {STEPS.map((label, i) => {
                const done = i < step;
                const active = i === step;
                const locked = i > step;
                return (
                    <StepCard key={i} locked={locked}>
                        <StepHeader active={active} done={done} onClick={() => done && !active && setStep(i)}>
                            <StepNumber active={active} done={done}>{i + 1}</StepNumber>
                            <div style={{ flex: 1 }}>
                                <StepTitle active={active} done={done}>{label}</StepTitle>
                                {done && <StepSummary>{stepSummary(i)}</StepSummary>}
                            </div>
                            {done && <EditLink>Rediger</EditLink>}
                        </StepHeader>

                        {active && (
                            <StepBody>
                                {renderStepBody(i)}
                                <StepActions>
                                    {i < STEPS.length - 1
                                        ? <PrimaryButton type="button" onClick={advanceStep}>Næste →</PrimaryButton>
                                        : <PrimaryButton type="button" onClick={checkOut}>Køb</PrimaryButton>
                                    }
                                </StepActions>
                            </StepBody>
                        )}
                    </StepCard>
                );
            })}
        </form>
    );

    return (
        <Overlay>
            <Modal>
                <ModalHeader>
                    <BackButton onClick={() => dispatch(changePage("ADDITIONAL"))}>
                        ← {tokens.BACK ?? "Tilbage"}
                    </BackButton>
                    <h2>{tokens.CHECKOUT}</h2>
                </ModalHeader>

                {cancelError && (
                    <ErrorBanner>
                        <span>Din betaling blev annulleret. Du kan prøve igen nedenfor.</span>
                        <button onClick={onCancelErrorDismiss} aria-label="Luk">×</button>
                    </ErrorBanner>
                )}

                {apiError && (
                    <ErrorBanner>
                        <span>{apiError}</span>
                        <button onClick={() => setApiError(null)} aria-label="Luk">×</button>
                    </ErrorBanner>
                )}

                <CheckoutBody>
                        <StepsCol>
                            {renderAccordion()}
                        </StepsCol>

                        <CartCol>
                            {cart.configimg && (
                                <CartImage src={`data:image/png;base64, ${cart.configimg}`} alt={tokens.YOUR_FLEXLINE} />
                            )}
                            <SectionLabel>{tokens.YOUR_CONFIGURATION ?? "Din konfiguration"}</SectionLabel>
                            {renderCartLines(products, "conf-")}

                            {cart.accessories && Object.keys(cart.accessories).length > 0 && <>
                                <SectionLabel>{tokens.ACCESSORIES}</SectionLabel>
                                {renderCartLines(cart.accessories, "acc-")}
                            </>}

                            {cart.additional && Object.keys(cart.additional).length > 0 && <>
                                <SectionLabel>{tokens.ADDITIONAL}</SectionLabel>
                                {renderCartLines(cart.additional, "add-")}
                            </>}

                            <CartTotals>
                                <PriceLine>
                                    <span>{tokens.YOUR_CONFIGURATION ?? "Konfiguration"}</span>
                                    <span>{fmt(configNumeric)}</span>
                                </PriceLine>
                                <PriceLine className="delivery">
                                    <span>{selectedMethod ? (tokens[selectedMethod.name] ?? selectedMethod.name) : ""}</span>
                                    <span>{fmt(deliveryCost)}</span>
                                </PriceLine>
                                <TotalLine>
                                    <span>{tokens.TOTAL_EX_MVA}</span>
                                    <strong>{fmt(grandTotal)}</strong>
                                </TotalLine>
                            </CartTotals>
                        </CartCol>
                    </CheckoutBody>
            </Modal>
        </Overlay>
    );
};

export default DanishCheckout;
